import {
  DatasetComponentOption,
  EChartsOption,
  TooltipComponentOption,
  SeriesOption,
} from 'echarts';
import { TranslateService } from '@ngx-translate/core';
import { ChartConfig } from '../chart.service';
import { ChartColorService } from '../chart-color.service';
import { max, min } from 'date-fns';
import { UnitSettingsV2Service, SchenckColors } from '@schenck/rotec-components';

/** Multitrend Chart */
export interface PlaneInfo {
  initialAbsolute: number;
  residualAbsolute: number;
  initialAngle?: number;
  residualAngle?: number;
}

export interface MultitrendValues {
  timestamp: Date;
  rotorId: string;
  planes: PlaneInfo[];
}

export interface MultitrendMeasure {
  asset: string;
  data: MultitrendValues[];
}

export interface MultitrendChartContent {
  rotorType: string;
  tolerances: number[];
  main: MultitrendMeasure;
  compare?: MultitrendMeasure;
}

export function getMultiTrendChartOptions({
  data,
  translate,
  colorService,
  settingsService,
  chartConfig,
  darkMode,
}: {
  data: MultitrendChartContent;
  translate: TranslateService;
  colorService: ChartColorService;
  settingsService: UnitSettingsV2Service;
  chartConfig?: ChartConfig;
  darkMode?: boolean;
}): EChartsOption[] {
  const config = chartConfig ? chartConfig : {};
  const selectedPlane = config.selectedPlane === undefined ? -1 : config.selectedPlane;
  const unbalUnit = settingsService.state.units.unbalanceValue;
  const decimals = settingsService.state.units.decimals;
  const tolerances: number[] = data.tolerances;
  const noOfPlanes = data.tolerances.length; // there is one value for each plane hence the length gives us the number of planes
  const assets = [data.main.asset, data.compare ? data.compare.asset : ''];

  const xAxisTimeName = 'xAxis-time';
  const xAxisToleranceName = 'xAxis-tolerance';
  const rotorIdName = 'rotorId-';
  const initialUnbalanceName = 'initialUnbalance-';
  const residualUnbalanceName = 'residualUnbalance-';
  const toleranceName = 'tolerance-0-';

  let tooltip: TooltipComponentOption = {};
  const datasetSource: any[] = [];
  const xAxisToleranceLimit: any[] = [];
  const toleranceData: any[] = [];

  const measures = [data.main.data];
  if (data.compare) {
    measures.push(data.compare.data);
  }

  measures
    .filter((item) => (data.compare?.data.length ? item.length : true))
    .forEach((measure, assetIndex) => {
      const xAxis: any[] = [xAxisTimeName + assetIndex];
      const rotorId: any[] = [rotorIdName + assetIndex];
      const initalUnbalances: any[] = [];
      const residualUnbalances: any[] = [];

      for (let i = 0; i < noOfPlanes; i++) {
        // asset number [0 = main | 1 = compare] - plane number
        const arrayName = assetIndex + '-' + i;
        initalUnbalances.push([initialUnbalanceName + arrayName]);
        residualUnbalances.push([residualUnbalanceName + arrayName]);
      }

      measure.forEach((yAxisValue) => {
        xAxis.push(yAxisValue.timestamp);
        rotorId.push(yAxisValue.rotorId);
        xAxisToleranceLimit.push(yAxisValue.timestamp);

        yAxisValue.planes.forEach((plane, planeIndex) => {
          plane.initialAbsolute
            ? initalUnbalances[planeIndex].push(plane.initialAbsolute.toFixed(decimals))
            : initalUnbalances[planeIndex].push(null);
          plane.residualAbsolute
            ? residualUnbalances[planeIndex].push(plane.residualAbsolute.toFixed(decimals))
            : residualUnbalances[planeIndex].push(null);
        });
      });
      datasetSource.push(xAxis, rotorId, ...initalUnbalances, ...residualUnbalances);
    });

  // The tolerance lines get a special xAxis with only the earliest and latest date
  const earliest = min(xAxisToleranceLimit);
  const latest = max(xAxisToleranceLimit);
  xAxisToleranceLimit.length = 0;
  xAxisToleranceLimit.push(xAxisToleranceName, earliest, latest);
  // Fill tolerance array with two values, one for earliest and one for latest date
  for (let i = 0; i < noOfPlanes; i++) {
    // xAxis reference array for plane i tolerances
    toleranceData.push([
      toleranceName + i,
      tolerances[i].toFixed(decimals),
      tolerances[i].toFixed(decimals),
    ]);
  }
  datasetSource.push(xAxisToleranceLimit, ...toleranceData);

  const dataset: DatasetComponentOption = {
    source: datasetSource,
  };

  tooltip = {
    formatter: (params: any) => {
      // we get all the infos we need from the param.data object which is the same for every param. its the defined dataset
      const param = params[0];
      let _tooltip = `${translate.instant('MC_Rotor Type: VALUE', { value: data.rotorType })}<br>`;

      // the last n + 1 entries are for the tolerances, one for each plane and one for the xAxis for the planes
      const iterateStep = 2 + noOfPlanes * 2;
      for (let i = 0; i < param.data.length - (noOfPlanes + 1); i += iterateStep) {
        if (selectedPlane === -1) {
          // all planes are selected
          _tooltip += param.data[i + 1]
            ? `${translate.instant('MC_Rotor ID: VALUE', { value: param.data[i + 1] })}<br>`
            : '';
        } else {
          _tooltip += param.data[i + 1]
            ? `${translate.instant('MC_Rotor ID: VALUE', { value: param.data[i + 1] })}<br>
            ${convertArrayName(param.dimensionNames[i + 2 + selectedPlane], assets)}:
            ${param.data[i + 2 + selectedPlane]} ${unbalUnit}<br>
            ${convertArrayName(param.dimensionNames[i + 2 + noOfPlanes + selectedPlane], assets)}:
            ${param.data[i + 2 + noOfPlanes + selectedPlane]} ${unbalUnit}<br>
            `
            : '';
        }
      }
      if (selectedPlane !== -1) {
        _tooltip += `
                ${convertArrayName(
                  param.dimensionNames[param.data.length - noOfPlanes + selectedPlane],
                  assets
                )}:
                ${tolerances[selectedPlane].toFixed(decimals)} ${unbalUnit}<br>`;
      }

      return _tooltip;
    },
    // important for the formatter to work. because of this params is an array
    trigger: 'axis',
    confine: true,
  };

  // The series defines which lines are shown in the graph
  const series: SeriesOption[] = <SeriesOption[]>datasetSource
    // filter out the arrays with values for xAxes
    .filter(
      (source) =>
        !source.some((el: any) =>
          typeof el === 'string' ? el.includes('xAxis') || el.includes('rotorId') : false
        )
    )
    .map((_source, index) => {
      const interval = noOfPlanes * 2 + 2; // noOfPlanes * (inital + residual unbalance for each plane) + (xAxis + rotorIds)
      return {
        type: 'line',
        name: convertArrayName(_source[0], assets, true),
        symbol: 'circle',
        symbolSize: _source[0].includes('tolerance') ? 0 : 8,
        seriesLayoutBy: 'row',
        encode: {
          x: _source[0].includes('tolerance')
            ? datasetSource.length - 4 // last 4 entries are for tolerance
            : index >= interval - 2 // index iterates over filtered array without xAxis and rotorIds
            ? interval
            : 0,
          y: _source[0], // name of dimension
        },
        lineStyle: {
          color: colorService.getMultiTrendColors(_source[0], noOfPlanes, darkMode),
          width: 4,
        },
        itemStyle: {
          color: colorService.getMultiTrendColors(_source[0], noOfPlanes, darkMode),
          borderColor: SchenckColors.SCHENCK_COLOR_WHITE,
          borderWidth: 2,
        },
      };
    })
    // filter for selected plane(s)
    .filter((_, index) => (selectedPlane === -1 ? true : (index - selectedPlane) % 3 === 0));

  function convertArrayName(arrayName: string, assetNames: string[], showAsset = false): string {
    const nameParts = arrayName.split('-');
    const name = nameParts[0];
    const asset: number = +nameParts[1];
    const plane: number = +nameParts[2];

    return `${showAsset ? assetNames[asset] + ':' : ''} ${translate.instant('MC_' + name)} ${
      plane === 0 ? translate.instant('MC_Static') : plane
    }`;
  }
  return [
    {
      grid: {
        top: 10,
        left: 42,
        bottom: 60,
        containLabel: true,
      },
      tooltip: tooltip,
      xAxis: {
        type: 'time',
        offset: 0,
        axisLine: {
          show: true,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        axisLabel: {
          hideOverlap: true,
        },
      },
      yAxis: {
        name: unbalUnit,
        nameLocation: 'end',
        nameTextStyle: {
          align: 'right',
          fontSize: 12,
          verticalAlign: 'top',
          width: 50,
          padding: [9, -42, 0, 0],
        },
        offset: 24,
        type: 'value',
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: true,
        },
      },
      dataset: dataset,
      series: series,
      legend: {
        orient: 'horizontal',
        type: 'scroll',
        bottom: 20,
        icon: 'circle',
        selected: config.selectedState ? config.selectedState : undefined,
      },
      dataZoom: config.zoom
        ? [
            {
              type: 'inside',
              xAxisIndex: [0],
              filterMode: 'none',
              start: config.zoomState ? config.zoomState.start : 0,
              end: config.zoomState ? config.zoomState.end : 100,
            },
          ]
        : [],
    },
  ];
}
